import { allPass, anyPass, filter, compose } from 'ramda';
import { LeagueContestantTypes, LeagueTypes, LeagueStates } from '@play/api-typings';

/**
 * Checks if roster is enabled on a league
 *
 * @param league
 */
const isRosterEnabled = (league) => league.enabledFeatures.indexOf("roster") !== -1;
/**
 * Checks if wire is enabled on a league
 *
 * @param league
 */
const isAnticheatEnabled = (league) => league.anticheatEnabled;
/**
 * Checks if ac2 is enabled on a league
 *
 * @param league
 */
const isAnticheat2Enabled = allPass([
    league => league.enabledFeatures.indexOf("ac2") !== -1,
    isAnticheatEnabled,
]);
/**
 * Checks if league allows entering result manually.
 * This is usually disabled in games with an integration.
 *
 * @param league
 */
const isEnterResultEnabled = (league) => league.matchSetupAllowed;
/**
 * Checks if league allows upload match media manually.
 *
 * @param league
 */
const isMatchMediaEnabled = (league) => league.matchMediaAllowed;
/**
 * Checks if league allows the contestants to ban maps.
 *
 * @param league
 */
const isMapVoteEnabled = (league) => league.mapvoteEnabled;
/**
 * Checks if you can manually sign off from a league.
 *
 * @param league
 */
const isSignOffEnabled = (league) => league.signOff.enabled;
/**
 * Checks if you can manually signup in league.
 *
 * @param league
 */
const isSignUpEnabled = (league) => league.signUp.enabled;
/**
 * Checks if league join is enabled and thus an admin needs to accept the signup.
 *
 * @param league
 */
const isLeagueJoinEnabled = (league) => league.signUp.verificationRequired;
/**
 * Checks if league ban is enabled on the league.
 * If yes, players that leave the league, will be banned for some days from that league
 *
 * @param league
 */
const isLeagueBanEnabled = (league) => league.kickBanDays > 0;

/**
 * Checks if league is in setup state
 *
 * @param league
 */
const isInSetup = (league) => league.state === "setup";
/**
 * Checks if league is in pre signup state
 *
 * @param league
 */
const isInPreSignUp = (league) => league.state === "preSignUp";
/**
 * Checks if league is in sigup state
 *
 * @param league
 */
const isInSignUp = (league) => league.state === "signUp";
/**
 * Checks if league is in post signup state
 *
 * @param league
 */
const isInPostSignUp = (league) => league.state === "postSignUp";
/**
 * Checks if league is in checkin state
 *
 * @param league
 */
const isInCheckIn = (league) => league.state === "checkIn";
/**
 * Checks if league is in late signup state
 *
 * @param league
 */
const isInLateSignUp = (league) => league.state === "lateSignUp";
/**
 * Checks if league is in progress
 *
 * @param league
 */
const isInProgress = (league) => league.state === "inProgress";
/**
 * Checks if league is finished
 *
 * @param league
 */
const isFinished = (league) => league.state === "finished";
/**
 * Checks if you could signup in the current league state
 *
 * @param league
 */
const isPossibleToSignUp = anyPass([isInSignUp, isInLateSignUp]);
/**
 * Checks if the league is upcoming
 *
 * @param league
 */
const isUpcoming = (league) => !isInProgress(league) && !isFinished(league);

/**
 * Checks if league is of type team league
 *
 * @param league
 */
const isTeam = (league) => league.contestantType === LeagueContestantTypes.Team;
/**
 * Checks if league is of type player league
 *
 * @param league
 */
const isPlayer = (league) => league.contestantType === LeagueContestantTypes.Player;
/**
 * Checks if league has a prizepool
 *
 * @param league
 */
const hasPrizePool = (league) => Boolean(league.prizePool);
/**
 * Checks if the league is of type cup
 *
 * @param league
 */
const isCup = (league) => league.type === "cup";
/**
 * Checks if the league is of type swiss
 *
 * @param league
 */
const isSwiss = (league) => league.type === "swiss";
/**
 * Checks if the league is of type premiership
 *
 * @param league
 */
const isPremiership = (league) => league.type === "premiership";
/**
 * Checks if the league is of type ladder
 *
 * @param league
 */
const isLadder = (league) => league.type === "ladder";
/**
 * Checks if the league is of type ffa
 *
 * @param league
 */
const isFFA = (league) => league.type === "ffa";
/**
 * Checks if the league is of type a-series
 *
 * @param league
 */
const isASeries = (league) => league.type === "a-series";
/**
 * Checks if a league is a long running tournament.
 * Long running tournaments allow teams to join while the tournament is in progress
 *
 * @param league
 */
const isLongRunning = anyPass([isLadder, isASeries]);
/**
 * Checks if a league is a short running tournament.
 * Short running tournaments do not allow teams to join while the tournament is in progress.
 *
 * @param league
 */
const isShortRunning = (league) => !isLongRunning(league);

/**
 * Get's all the leagues that have a prize
 *
 * @param leagues
 */
const getWithPrizes = filter(hasPrizePool);
/**
 * Get all cups from a list of leagues
 *
 * @param leagues
 */
const getCups = filter(isCup);

/**
 * Get the id of a league
 *
 * @param league
 */
const getId = (league) => league.id;
/**
 * Get the uri of a league
 *
 * @param league
 */
const getUri = (league) => league.uri;
/**
 * Get the prize pool of a league
 *
 * @param league
 */
const getPrizePool = (league) => league.prizePool;
/**
 * Gets the league's timeline
 *
 * @param league
 */
const getTimeline = (league) => league.timeline;
/**
 * Gets the signup timeline
 *
 * @param timeline
 */
const getSignUpTimeline = (timeline) => (timeline ? timeline.signUp : null);
/**
 * Gets the checkin timeline
 *
 * @param timeline
 */
const getCheckInTimeline = (timeline) => (timeline ? timeline.checkIn : null);
/**
 * Gets the late signup timeline
 *
 * @param timeline
 */
const getLateSignUpTimeline = (timeline) => timeline ? timeline.lateSignUp : null;
/**
 * Gets the in progress timeline
 *
 * @param timeline
 */
const getInProgressTimeline = (timeline) => timeline ? timeline.inProgress : null;
/**
 * Gets the begin date of an timeline item
 *
 * @param timeline
 */
const getBeginOfTimeline = (timeline) => (timeline ? timeline.begin : null);
/**
 * Gets the end date of an timeline item
 *
 * @param timeline
 */
const getEndOfTimeline = (timeline) => (timeline ? timeline.end : null);
/**
 * Gets the league's start date
 */
const getStartDate = compose(getBeginOfTimeline, getInProgressTimeline, getTimeline);
/**
 * Gets the league's signup start date
 */
const getSignupStartDate = compose(getBeginOfTimeline, getSignUpTimeline, getTimeline);
/**
 * Gets the league's checkin start date
 */
const getCheckInStartDate = compose(getBeginOfTimeline, getCheckInTimeline, getTimeline);
/**
 * Gets the league's late signup start date
 */
const getLateSignupStartDate = compose(getBeginOfTimeline, getLateSignUpTimeline, getTimeline);
/**
 * Gets the league's end date
 */
const getEndDate = compose(getEndOfTimeline, getInProgressTimeline, getTimeline);
/**
 * Gets the league's signup end date
 */
const getSignupEndDate = compose(getEndOfTimeline, getSignUpTimeline, getTimeline);
/**
 * Gets the league's checkin end date
 */
const getCheckInEndDate = compose(getEndOfTimeline, getCheckInTimeline, getTimeline);
/**
 * Gets the league's late signup end date
 */
const getLateSignupEndDate = compose(getEndOfTimeline, getLateSignUpTimeline, getTimeline);
const getPlayerCount = (league) => {
    if (league.type === LeagueTypes.Cup ||
        league.type === LeagueTypes.Premiership ||
        league.type === LeagueTypes.Swiss ||
        league.type === LeagueTypes.FFA) {
        if (league.state !== LeagueStates.Setup && league.state !== LeagueStates.InProgress && league.state !== LeagueStates.Finished) {
            return league.contestants.signedUp;
        }
        return league.contestants.checkedIn;
    }
    return league.contestants.signedUp;
};
const isCancelled = (league) => {
    return (["swiss", "premiership", "ffa", "cup"].indexOf(league.type) !== -1 && league.state === LeagueStates.Finished && !getEndDate(league));
};

const hasIntegration = (league, integration) => league.gameIntegration && league.gameIntegration.split(",").indexOf(integration) !== -1;

/**
 * Generated bundle index. Do not edit.
 */

export { getBeginOfTimeline, getCheckInEndDate, getCheckInStartDate, getCheckInTimeline, getCups, getEndDate, getEndOfTimeline, getId, getInProgressTimeline, getLateSignUpTimeline, getLateSignupEndDate, getLateSignupStartDate, getPlayerCount, getPrizePool, getSignUpTimeline, getSignupEndDate, getSignupStartDate, getStartDate, getTimeline, getUri, getWithPrizes, hasIntegration, hasPrizePool, isASeries, isAnticheat2Enabled, isAnticheatEnabled, isCancelled, isCup, isEnterResultEnabled, isFFA, isFinished, isInCheckIn, isInLateSignUp, isInPostSignUp, isInPreSignUp, isInProgress, isInSetup, isInSignUp, isLadder, isLeagueBanEnabled, isLeagueJoinEnabled, isLongRunning, isMapVoteEnabled, isMatchMediaEnabled, isPlayer, isPossibleToSignUp, isPremiership, isRosterEnabled, isShortRunning, isSignOffEnabled, isSignUpEnabled, isSwiss, isTeam, isUpcoming };
//# sourceMappingURL=play-utility-league.js.map
